import { Link } from "react-router-dom";
import PageShell from "@/components/PageShell";
import usePricing from "@/hooks/usePricing";
import { fillPrices } from "@/priceTokens";
import { SITE } from "@/seoData";
import { STEPS, WHY } from "@/siteContent";
import { HOME_EXTRA } from "@/seoContentV4";
import { V5_HOME_EXTRA } from "@/seoContentV5";

const H2 = ({ children }) => <h2 className="font-display font-black tracking-tighter text-3xl lg:text-4xl text-[#15306E]">{children}</h2>;

export default function About() {
  const pricing = usePricing();
  const canonical = `${SITE}/about`;
  const seo = {
    title: "How Our Junk Removal Works | About CleanOut247 San Diego",
    description: "How CleanOut247 junk removal works in three steps, and why San Diego County homeowners and businesses choose us: upfront prices, same-day pickup, licensed and insured.",
    canonical,
    jsonLd: [
      { "@context": "https://schema.org", "@type": "AboutPage", name: "About CleanOut247", url: canonical, about: { "@id": SITE + "/#business" } },
      { "@context": "https://schema.org", "@type": "HowTo", name: "How to book junk removal with CleanOut247", step: STEPS.map((s, i) => ({ "@type": "HowToStep", position: i + 1, name: s.t, text: s.d })) },
      { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE + "/" },
        { "@type": "ListItem", position: 2, name: "How It Works", item: canonical },
      ] },
    ],
  };
  return (
    <PageShell seo={seo} eyebrow="How It Works" h1="How CleanOut247 Junk Removal Works"
      lead="CleanOut247 is a local junk removal company serving North San Diego, North County and all of San Diego County. Here's how a pickup works and why customers call us back.">
      <section className="px-5 lg:px-8 py-16" data-testid="about-steps">
        <div className="max-w-5xl mx-auto">
          <H2>Three steps. Zero hassle.</H2>
          <div className="mt-8 grid md:grid-cols-3 gap-5">
            {STEPS.map((s) => (
              <div key={s.n} data-testid={`step-${s.n}`} className="border border-[#E4E4E7] rounded-sm p-6">
                <span className="font-display font-black text-3xl text-[#46A01E] tracking-tighter">{s.n}</span>
                <h3 className="font-display font-extrabold text-xl text-[#15306E] mt-2">{s.t}</h3>
                <p className="text-[#52525B] mt-2 leading-relaxed text-sm">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="px-5 lg:px-8 py-16 bg-[#F4FAF0]" data-testid="about-why">
        <div className="max-w-5xl mx-auto">
          <H2>Why choose CleanOut247</H2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {WHY.map((w) => (
              <div key={w.t} data-testid={`why-card-${w.t}`} className="bg-white border border-[#E4E4E7] rounded-sm p-6">
                <h3 className="font-display font-extrabold text-lg text-[#15306E]">{w.t}</h3>
                <p className="text-sm text-[#52525B] mt-2 leading-relaxed">{w.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="px-5 lg:px-8 py-16">
        <div className="max-w-3xl mx-auto space-y-8">
          {[...HOME_EXTRA, ...V5_HOME_EXTRA].map((x) => (
            <div key={x.h2}>
              <h2 className="font-display font-black tracking-tighter text-2xl lg:text-3xl text-[#15306E]">{x.h2}</h2>
              <p className="text-[#52525B] mt-3 leading-relaxed">{fillPrices(x.text, pricing)}</p>
            </div>
          ))}
          <p className="text-[#52525B]">See our <Link to="/junk-removal-cost" className="text-[#46A01E] font-semibold underline">junk removal prices</Link> or read the <Link to="/faq" className="text-[#46A01E] font-semibold underline">FAQ</Link>.</p>
        </div>
      </section>
    </PageShell>
  );
}
